import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  ReceiptPrintPortal,
} from '@mlm/ui';
import {
  fetchOrderReceipt,
  fetchReport,
  getTenantFeatures,
  type AdminUserSearchResult,
  type BranchTypeFilter,
  type ReportParams,
  type ReportResult,
} from '../lib/api';
import { downloadReport, type ReportExportMeta } from '../lib/report-export';
import { ReportExportButtons } from '../components/report-export-buttons';
import { BranchCheckboxList, BranchTypePicker } from '../components/branch-picker';
import { useBranchOptions, type BranchSelection } from '../lib/branch-scope';
import { MemberPicker } from '../components/member-picker';

/**
 * Phase 3 admin reports — a registry-driven picker. Pick a report, set the date
 * window (and a slot code / reason where the report needs one), view the table,
 * and export the exact same data as CSV. Every report shares one generic
 * `{ columns, rows, summary }` renderer.
 */
/** `user` picks a MEMBER (not a slot) and reports across every slot they own. */
type ReportKind = 'window' | 'none' | 'member' | 'movements' | 'group' | 'user';
/** `branch` marks the reports the server narrows by `?branchId=` — which takes a
 *  comma-separated LIST, so these get a multi-select checkbox row. It cuts
 *  across the kinds, so it's a flag rather than another kind. Must match
 *  BRANCH_WINDOW_REPORTS + the two inventory routes in
 *  apps/api/src/routes/reports.ts.
 *
 *  `branchType` marks the reports that additionally honour `?branchType=`
 *  (branch vs stockist). Today that's Stock on Hand alone — see
 *  `stockQuerySchema` in the same route file. */
type ReportDef = {
  key: string;
  label: string;
  group: string;
  kind: ReportKind;
  branch?: true;
  branchType?: true;
  /** Which payment-method registry feeds this report's method dropdown. */
  paymentMethod?: 'pos' | 'ecom';
  /** Renders the include-imported-sales checkbox. */
  imports?: true;
  /**
   * Rows open the POS receipt on click. Only reports whose rows carry an
   * `order_id` AND are POS orders — an ecom order has no `pos_payments` row, so
   * its tape would print with no tender line.
   */
  receipt?: true;
  /**
   * Renders the From/To range on a report whose KIND doesn't already imply one.
   * Needed because Member Total Earned takes both a member picker and a range,
   * and `kind` can only say one thing.
   */
  window?: true;
  /**
   * A balance snapshot that ages as of a DAY: renders the As-of date picker and
   * sends `asOf=YYYY-MM-DD` (tenant-local, audit F23). Blank = today.
   */
  asOf?: true;
  /** Due Payments: renders the "due within" window select (`withinDays`). */
  dueWithin?: true;
};

const REPORTS: ReportDef[] = [
  {
    key: 'pos-sales',
    label: 'POS Sales',
    group: 'Sales',
    kind: 'window',
    branch: true,
    paymentMethod: 'pos',
    imports: true,
    receipt: true,
  },
  {
    key: 'ecom-sales',
    label: 'Ecom Sales',
    group: 'Sales',
    kind: 'window',
    branch: true,
    paymentMethod: 'ecom',
    imports: true,
  },
  { key: 'ecom-orders', label: 'Ecom Orders', group: 'Sales', kind: 'window', branch: true },
  { key: 'sales-all', label: 'Sales (All)', group: 'Sales', kind: 'window', branch: true },
  {
    key: 'product-movement',
    label: 'Product Movement',
    group: 'Sales',
    kind: 'window',
    branch: true,
  },
  // `group`: needs a slot code AND a date window — the `member` kind shows the
  // slot input but hides the window.
  { key: 'group-purchases', label: 'Group Purchases', group: 'Sales', kind: 'group' },
  { key: 'payout', label: 'Payouts', group: 'Finance', kind: 'window' },
  { key: 'member-earnings', label: 'Member Earnings', group: 'Members', kind: 'window' },
  { key: 'member-wallet', label: 'Member Wallets', group: 'Members', kind: 'none' },
  { key: 'member-pv', label: 'Member Point Value', group: 'Members', kind: 'window' },
  { key: 'member-details', label: 'Member Details', group: 'Members', kind: 'window' },
  { key: 'course-engagement', label: 'Course Engagement', group: 'Learning', kind: 'window' },
  { key: 'course-sales', label: 'Course Sales', group: 'Learning', kind: 'window' },
  // `user` (not `member`): reports across EVERY slot the member owns, so it
  // takes a member rather than one slot code. The range is OPTIONAL and changes
  // what Total Earned means — undated it's the lifetime wallet figure (which
  // ties out with the member's own portal), dated it's the ledger inside the
  // window. See `memberTotalEarnedReport`.
  {
    key: 'member-total-earned',
    label: 'Member Total Earned',
    group: 'Members',
    kind: 'user',
    window: true,
  },
  { key: 'member-detailed-wallet', label: 'Member Wallet Log', group: 'Members', kind: 'member' },
  { key: 'member-binary-points', label: 'Member Binary Points', group: 'Members', kind: 'member' },
  { key: 'referral', label: 'Referral Summary', group: 'Network', kind: 'window' },
  { key: 'direct-referral', label: 'Direct Referrals', group: 'Network', kind: 'window' },
  { key: 'downline-detail', label: 'Downline Detail', group: 'Network', kind: 'window' },
  // `member` (slot code in, no date window): one slot's whole genealogy —
  // Name / Email / Slot Code / Level — with the true subtree size in the
  // summary strip. The server also accepts `tree=binary`, deliberately NOT
  // exposed: "my downline" here means the sponsor tree.
  { key: 'downline-list', label: 'Downline List (by slot)', group: 'Network', kind: 'member' },
  // `window` (not `none`): with no dates this is still the live snapshot, but
  // supplying a range switches the server to an Opening/In/Out/Closing stock
  // card. Flipping the kind is the whole mechanism for showing the date inputs.
  {
    key: 'inventory-stock',
    label: 'Stock on Hand',
    group: 'Inventory',
    kind: 'window',
    branch: true,
    branchType: true,
  },
  {
    key: 'inventory-movements',
    label: 'Stock Movements',
    group: 'Inventory',
    kind: 'movements',
    branch: true,
  },
  // Cafe POS. Each hides unless its tenant switch is on (the picker filter below).
  {
    key: 'material-usage',
    label: 'Material Usage & Cost',
    group: 'Inventory',
    kind: 'window',
    branch: true,
  },
  // A snapshot of the open lots: no date window.
  {
    key: 'stock-lots',
    label: 'Stock Lots (cost & expiry)',
    group: 'Inventory',
    kind: 'none',
    branch: true,
  },
  // Accounting v1 — the group hides unless `accounting_enabled` (and the
  // consignment report additionally needs `consignment_enabled`), mirroring
  // the Learning/lmsEnabled gate below.
  {
    key: 'expense-by-category',
    label: 'Expenses by Category',
    group: 'Accounting',
    kind: 'window',
  },
  // As-of snapshots: blank ages as of today, a picked day ages as of that
  // day's close.
  { key: 'ap-aging', label: 'Payables Aging', group: 'Accounting', kind: 'none', asOf: true },
  // The bills to pay, earliest due first: overdue plus due within the window.
  {
    key: 'ap-due',
    label: 'Due Payments (bills to pay)',
    group: 'Accounting',
    kind: 'none',
    asOf: true,
    dueWithin: true,
  },
  // R2: the AR side. `ar-aging` summarises per customer, `open-invoices` is the
  // document-level backing for it.
  {
    key: 'ar-aging',
    label: 'Receivables Aging',
    group: 'Accounting',
    kind: 'none',
    asOf: true,
  },
  { key: 'open-invoices', label: 'Open Billings', group: 'Accounting', kind: 'none', asOf: true },
  {
    key: 'consignment-outstanding',
    label: 'Consignment Outstanding',
    group: 'Accounting',
    kind: 'none',
  },
  // Accounting Phase 1 (cost/COGS). Valuation is a live snapshot — no window;
  // turnover is units sold in the window vs current stock. Both exclude
  // stockist-owned branches server-side.
  {
    key: 'inventory-valuation',
    label: 'Inventory Valuation (at cost)',
    group: 'Accounting',
    kind: 'none',
    branch: true,
  },
  {
    key: 'stock-turnover',
    label: 'Stock Turnover (units)',
    group: 'Accounting',
    kind: 'window',
    branch: true,
  },
  // VAT per sale document — house orders, billings (AR-, both kinds) and
  // stockist billings (SAR-), i.e. everything the GL books Output VAT for.
  // "Untagged" = sales recorded before House VAT was enabled.
  {
    key: 'vat-sales',
    label: 'VAT Sales (VATable / Exempt / Zero-rated)',
    group: 'Accounting',
    kind: 'window',
    branch: true,
  },
  // Phase 4 lean GL — live statements over posted journal lines.
  { key: 'trial-balance', label: 'Trial Balance', group: 'Accounting', kind: 'none', asOf: true },
  { key: 'income-statement', label: 'Income Statement', group: 'Accounting', kind: 'window' },
  { key: 'balance-sheet', label: 'Balance Sheet', group: 'Accounting', kind: 'none', asOf: true },
  { key: 'journal-export', label: 'Journal Export (lines)', group: 'Accounting', kind: 'window' },
  {
    key: 'customer-deposits-open',
    label: 'Customer Deposits — Open Items',
    group: 'Accounting',
    kind: 'none',
    asOf: true,
  },
];

const GROUPS = ['Sales', 'Finance', 'Members', 'Network', 'Inventory', 'Learning', 'Accounting'];

/**
 * Reports that cut on a tenant-local CALENDAR DATE rather than an instant
 * (audit F23). They get a plain date picker and their day is sent through
 * untouched: converting it here produced an instant in the BROWSER's timezone,
 * which moved the cut a day for any admin outside the tenant's own timezone.
 * Everything else keeps the datetime-local picker and the ISO instant.
 */
const GL_DATE_REPORTS = new Set(['income-statement', 'journal-export', 'gl-account']);

const MOVEMENT_REASONS = [
  'receive',
  'adjustment',
  'write_off',
  'void',
  'pos_sale',
  'ecom_sale',
  'refund_restock',
  'cancel_restock',
  'stockist_purchase',
  'consignment_out',
  'consignment_return',
  'supplier_return',
  'stockist_consign_in',
  'stockist_consign_return',
  'bundle_open',
  'material_consume',
  'material_restock',
];

export function ReportsPage() {
  const [selectedKey, setSelectedKey] = useState<string>('ecom-orders');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Tenant-local YYYY-MM-DD for the as-of reports; '' = today.
  const [asOf, setAsOf] = useState('');
  const [withinDays, setWithinDays] = useState('30');
  const [slotCode, setSlotCode] = useState('');
  const [reason, setReason] = useState('');
  // Tri-state, see `BranchSelection`: `[]` = nothing ticked (the report waits),
  // `null` = "All" (no `branchId` param, tenant-wide), a list = those branches.
  // Starts empty on purpose — a branch-capable report should not run tenant-wide
  // before anyone said which branch they meant.
  const [branchIds, setBranchIds] = useState<BranchSelection>([]);
  const [branchType, setBranchType] = useState<BranchTypeFilter | undefined>();
  const [tree, setTree] = useState<'unilevel' | 'binary'>('unilevel');
  const [includeSelf, setIncludeSelf] = useState(false);
  const [pickedUser, setPickedUser] = useState<AdminUserSearchResult | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [includeImports, setIncludeImports] = useState(false);
  // Order whose receipt is open. Rows carry `order_id` outside `columns`, so it
  // is invisible in the table and the CSV but still identifies the sale.
  const [receiptOrderId, setReceiptOrderId] = useState<string | null>(null);

  // The method dropdown reads the admin-managed registries straight off tenant
  // features — no dedicated endpoint. Known limitation: a code since REMOVED
  // from the registry can't be filtered to, though its orders still appear
  // (with their stored label) on an unfiltered run.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  // LMS master switch: the Learning report group is absent when the tenant
  // has learning off.
  const lmsEnabled = featuresQ.data?.lms_enabled === true;
  // Accounting v1: same pattern for the Accounting group + consignment report.
  const accountingEnabled = featuresQ.data?.accounting_enabled === true;
  const consignmentEnabled = featuresQ.data?.consignment_enabled === true;
  const materialsEnabled = featuresQ.data?.materials_enabled === true;
  const stockLotsEnabled = featuresQ.data?.inventory_lots_enabled === true;

  const def = REPORTS.find((r) => r.key === selectedKey)!;
  const isGroup = def.kind === 'group';
  const needsSlot = def.kind === 'member' || isGroup;
  const needsUser = def.kind === 'user';
  // `window` is a FLAG, not a kind: Member Total Earned takes a member picker
  // (kind 'user') AND an optional range, and `kind` is already overloaded three
  // ways. Reports that are windowed by virtue of their kind keep working.
  const showWindow =
    def.kind === 'window' || def.kind === 'movements' || isGroup || def.window === true;
  const glDates = GL_DATE_REPORTS.has(def.key);

  // The branch control hides itself when there is nothing to choose (an operator
  // pinned to one branch, or no branch matches the type filter). Treat that as
  // "All" — otherwise those operators would face a run gate they can never pass.
  // While the branch list is still loading we know neither, so the gate waits.
  const branchOpts = useBranchOptions(branchType);
  const branchSel: BranchSelection = def.branch && !branchOpts.hidden ? branchIds : null;
  const branchUndecided = !!def.branch && branchOpts.isPending;
  const branchChosen =
    !!def.branch && !branchUndecided && (branchSel === null || branchSel.length > 0);

  const params: ReportParams = {
    // `.slice(0, 10)` because switching between a date-cut report and an
    // instant one leaves the other picker's value in state, and a stale
    // `2026-08-30T10:00` matches neither accepted shape server-side.
    ...(showWindow && from
      ? { from: glDates ? from.slice(0, 10) : new Date(from).toISOString() }
      : {}),
    ...(showWindow && to ? { to: glDates ? to.slice(0, 10) : new Date(to).toISOString() } : {}),
    ...(needsSlot && slotCode ? { slotCode: slotCode.trim() } : {}),
    ...(needsUser && pickedUser ? { userId: pickedUser.id } : {}),
    ...(def.kind === 'movements' && reason ? { reason } : {}),
    // Comma-joined, not an array: axios has no paramsSerializer here, so an
    // array would go out as `branchId[]=` and the server would never see it.
    // `null` (= All) deliberately sends NOTHING: the server's no-param default
    // also covers orders with no branch, which an explicit `IN (…)` list drops.
    ...(def.branch && branchSel?.length ? { branchId: branchSel.join(',') } : {}),
    ...(def.branchType && branchType ? { branchType } : {}),
    ...(def.paymentMethod && paymentMethod ? { paymentMethod } : {}),
    ...(def.imports && includeImports ? { includeImports: 'true' as const } : {}),
    ...(def.asOf && asOf ? { asOf } : {}),
    ...(def.dueWithin ? { withinDays } : {}),
    ...(isGroup ? { tree, includeSelf: includeSelf ? 'true' : 'false' } : {}),
  };
  // An inverted range is clamped to an empty window server-side rather than
  // 500ing, but reporting "nothing moved" for a typo is worse than saying so.
  const rangeInvalid = showWindow && !!from && !!to && new Date(to) < new Date(from);
  const ready =
    (!needsSlot || !!slotCode.trim()) &&
    (!needsUser || !!pickedUser) &&
    (!def.branch || branchChosen) &&
    !rangeInvalid;

  // Why the report isn't running yet. One hardcoded line used to say "Enter a
  // slot code" for every case, which was already wrong for the member reports.
  const notReadyReason = needsSlot
    ? 'Enter a slot code to run this report.'
    : needsUser
      ? 'Pick a member to run this report.'
      : def.branch && !branchChosen
        ? 'Tick a branch — or “All” — to run this report.'
        : null;

  const reportQ = useQuery({
    // `branchSel` rides the key explicitly: All (`null`) and nothing-ticked
    // (`[]`) both omit `branchId` from `params`, so without it the two states
    // would collide on one cache entry.
    queryKey: ['report', selectedKey, params, branchSel],
    queryFn: () => fetchReport(selectedKey, params),
    enabled: ready,
  });

  // Context lines that ride into the PDF header and the workbook's Info sheet,
  // so a file that leaves the admin still says which report it is and how it
  // was filtered. `filenameBase` is deliberately left to the builder, which
  // slugs the title at CLICK time (a page left open overnight must not stamp
  // yesterday's date on today's download).
  const exportMeta: ReportExportMeta = {
    title: def.label,
    sheetName: def.label,
    subtitle: [
      def.asOf
        ? `As of: ${asOf || 'today'}`
        : showWindow && (from || to)
          ? `Period: ${from || 'start'} → ${to || 'now'}`
          : 'Period: all',
      ...(needsSlot && slotCode.trim() ? [`Slot: ${slotCode.trim()}`] : []),
      ...(needsUser && pickedUser ? [`Member: ${pickedUser.fullName ?? pickedUser.email}`] : []),
      ...(def.branch && branchSel?.length
        ? [`Branches: ${branchSel.length} selected`]
        : def.branch
          ? ['Branches: all']
          : []),
      ...(def.branchType && branchType ? [`Branch type: ${branchType}`] : []),
      ...(def.paymentMethod && paymentMethod ? [`Payment method: ${paymentMethod}`] : []),
      ...(def.imports && includeImports ? ['Includes imported legacy sales'] : []),
      ...(isGroup ? [`Downline: ${tree}${includeSelf ? ' (incl. own purchases)' : ''}`] : []),
      ...(def.kind === 'movements' && reason ? [`Reason: ${reason}`] : []),
    ],
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]">
      {/* Report picker */}
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="text-base">Reports</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {GROUPS.filter(
            (g) => (g !== 'Learning' || lmsEnabled) && (g !== 'Accounting' || accountingEnabled),
          ).map((g) => (
            <div key={g} className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {g}
              </p>
              {REPORTS.filter(
                (r) =>
                  r.group === g &&
                  (r.key !== 'consignment-outstanding' || consignmentEnabled) &&
                  (r.key !== 'material-usage' || materialsEnabled) &&
                  (r.key !== 'stock-lots' || stockLotsEnabled),
              ).map((r) => (
                <button
                  key={r.key}
                  onClick={() => {
                    setSelectedKey(r.key);
                    // Branch changes RESULTS, so it can't quietly carry over to a
                    // report that ignores it and then reapply on the way back.
                    if (!r.branch) setBranchIds([]);
                    if (!r.branchType) setBranchType(undefined);
                    if (r.kind !== 'user') setPickedUser(null);
                    // The range changes RESULTS too, and on Member Total Earned
                    // it changes what a COLUMN MEANS — carrying a range over
                    // from another report would silently switch that report to
                    // its ledger-only definition with nothing on screen touched.
                    if (
                      !(
                        r.kind === 'window' ||
                        r.kind === 'movements' ||
                        r.kind === 'group' ||
                        r.window
                      )
                    ) {
                      setFrom('');
                      setTo('');
                    }
                    // Both change RESULTS, so neither may quietly carry over to
                    // a report that ignores it and reapply on the way back.
                    if (!r.paymentMethod) setPaymentMethod('');
                    if (!r.imports) setIncludeImports(false);
                  }}
                  className={
                    'block w-full rounded-md px-2 py-1 text-left text-sm transition-colors ' +
                    (r.key === selectedKey
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Controls + results */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle>{def.label}</CardTitle>
            <ReportExportButtons
              result={reportQ.data}
              meta={exportMeta}
              disabled={!ready}
              onCsv={() =>
                downloadReport(selectedKey, params, 'csv', {
                  ...exportMeta,
                  result: reportQ.data,
                })
              }
            />
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-2">
              {showWindow ? (
                <>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">From</p>
                    <Input
                      type={glDates ? 'date' : 'datetime-local'}
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">To</p>
                    <Input
                      type={glDates ? 'date' : 'datetime-local'}
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setFrom('');
                      setTo('');
                    }}
                  >
                    Clear
                  </Button>
                </>
              ) : null}
              {def.asOf ? (
                <>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">As of</p>
                    <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
                  </div>
                  {asOf ? (
                    <Button variant="ghost" onClick={() => setAsOf('')}>
                      Today
                    </Button>
                  ) : (
                    <p className="pb-2 text-xs text-muted-foreground">Blank = today</p>
                  )}
                </>
              ) : null}
              {def.dueWithin ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Due within</p>
                  <select
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    value={withinDays}
                    onChange={(e) => setWithinDays(e.target.value)}
                  >
                    {['0', '7', '14', '30', '60', '90'].map((d) => (
                      <option key={d} value={d}>
                        {d === '0' ? 'Overdue and due today' : `${d} days`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {def.branchType ? (
                <BranchTypePicker
                  value={branchType}
                  onChange={(v) => {
                    setBranchType(v);
                    // Drop a now-contradictory branch selection, so the two
                    // controls can never disagree into a silently empty report.
                    // Back to nothing-ticked, so the report waits for a fresh
                    // pick rather than quietly rerunning against a wider set.
                    setBranchIds([]);
                  }}
                />
              ) : null}
              {def.branch ? (
                <BranchCheckboxList
                  selected={branchIds}
                  onChange={setBranchIds}
                  filterType={branchType}
                />
              ) : null}
              {needsSlot ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Slot code</p>
                  <Input
                    placeholder="e.g. ADMELIC…"
                    value={slotCode}
                    onChange={(e) => setSlotCode(e.target.value)}
                  />
                </div>
              ) : null}
              {def.paymentMethod ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payment method</p>
                  <select
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="">All methods</option>
                    {(def.paymentMethod === 'pos'
                      ? (featuresQ.data?.pos_payment_methods?.methods ?? [])
                      : (featuresQ.data?.ecom_payment_methods?.methods ?? [])
                    ).map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.label}
                        {!m.enabled && ' · disabled'}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {def.imports ? (
                <label className="flex h-10 items-center gap-2 text-sm">
                  <Checkbox
                    checked={includeImports}
                    onCheckedChange={(v) => setIncludeImports(v === true)}
                  />
                  Include imported legacy sales
                </label>
              ) : null}
              {needsUser ? (
                <div className="min-w-[280px]">
                  <p className="text-xs text-muted-foreground mb-1">Member</p>
                  <MemberPicker
                    selected={pickedUser}
                    onChange={setPickedUser}
                    placeholder="Search name, email or referral code…"
                  />
                </div>
              ) : null}
              {isGroup ? (
                <>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Downline</p>
                    <select
                      className="h-10 rounded-md border bg-background px-3 text-sm"
                      value={tree}
                      onChange={(e) => setTree(e.target.value as 'unilevel' | 'binary')}
                    >
                      <option value="unilevel">Sponsor tree (recruited)</option>
                      <option value="binary">Binary tree (incl. spillover)</option>
                    </select>
                  </div>
                  <label className="flex h-10 items-center gap-2 text-sm">
                    <Checkbox
                      checked={includeSelf}
                      onCheckedChange={(v) => setIncludeSelf(v === true)}
                    />
                    Include this slot&rsquo;s own purchases
                  </label>
                </>
              ) : null}
              {def.kind === 'movements' ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Reason</p>
                  <select
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    <option value="">All reasons</option>
                    {MOVEMENT_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {rangeInvalid ? (
              <p className="p-6 text-sm text-destructive">“To” must be on or after “From”.</p>
            ) : !ready ? (
              <p className="p-6 text-sm text-muted-foreground">{notReadyReason ?? 'Loading…'}</p>
            ) : reportQ.isLoading ? (
              <p className="p-6 text-sm text-muted-foreground">Loading…</p>
            ) : reportQ.error ? (
              <p className="p-6 text-sm text-destructive">Failed to load the report.</p>
            ) : !reportQ.data?.rows.length ? (
              <p className="p-6 text-sm text-muted-foreground">No data for this report / range.</p>
            ) : (
              <>
                {isGroup ? (
                  <p className="border-b px-6 py-2 text-xs text-muted-foreground">
                    One row per order line. <strong>Ttl Amt</strong> is the line total;{' '}
                    <strong>Subtotal</strong> is the whole order&rsquo;s and repeats on every line
                    of a multi-line sale — sum Ttl Amt, not Subtotal.
                  </p>
                ) : null}
                <ReportTable
                  data={reportQ.data}
                  onRowClick={
                    def.receipt
                      ? (row) => {
                          const id = row.order_id;
                          if (typeof id === 'string' && id) setReceiptOrderId(id);
                        }
                      : undefined
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <ReceiptDialog orderId={receiptOrderId} onClose={() => setReceiptOrderId(null)} />
    </div>
  );
}

/**
 * The receipt behind a sales row.
 *
 * Renders through `ReceiptPrintPortal` (a body-level portal), NOT inside a
 * Dialog: the print stylesheet isolates `.print-root` from its body-level
 * siblings, and Radix portals dialog content to <body> too — so a tape inside a
 * dialog would be collapsed along with everything else.
 */
function ReceiptDialog({ orderId, onClose }: { orderId: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['admin-order-receipt', orderId],
    queryFn: () => fetchOrderReceipt(orderId!),
    enabled: !!orderId,
  });
  if (!orderId) return null;
  if (!q.data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="rounded-md bg-background px-6 py-4 text-sm text-muted-foreground">
          {q.error ? 'Could not load this receipt.' : 'Loading receipt…'}
          <button className="ml-3 underline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }
  return (
    <ReceiptPrintPortal
      data={q.data}
      caption={`Sale ${q.data.orderCode} — activation PINs are withheld on this copy`}
      onClose={onClose}
    />
  );
}

function ReportTable({
  data,
  onRowClick,
}: {
  data: ReportResult;
  onRowClick?: (row: ReportResult['rows'][number]) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b">
            <tr>
              {data.columns.map((c) => (
                <th
                  key={c.key}
                  className={'px-3 py-2 font-medium ' + (c.align === 'right' ? 'text-right' : '')}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr
                key={i}
                className={
                  'border-b last:border-0 ' + (onRowClick ? 'cursor-pointer hover:bg-muted/40' : '')
                }
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {data.columns.map((c) => (
                  <td
                    key={c.key}
                    className={
                      'px-3 py-1.5 text-xs ' +
                      (c.align === 'right' ? 'text-right font-mono tabular-nums' : '')
                    }
                  >
                    {row[c.key] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.summary?.length ? (
        <div className="flex flex-wrap gap-x-6 gap-y-1 border-t px-3 py-3 text-xs">
          {data.summary.map((s) => (
            <span key={s.label}>
              <span className="text-muted-foreground">{s.label}: </span>
              <span className="font-medium tabular-nums">{s.value}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
